import React, { useContext, useMemo, useState } from 'react';
import './SubscriptionPlanCards.less';
import { IEquipmentEntry } from 'types/IEquipmentEntry';
import { api } from 'services/api';
import {
  BillingPeriod,
  PlanPricing,
  PremiumPlanInfoTooltip,
  useBasicBenefitsList,
  usePremiumBenefitsList,
} from 'app/components/subscriptions/dialogs/ManageYourPlanDialog';
import {
  BillingSwitch,
  PremiumInfoPlanCard,
  SubscriptionPlanComparisonGrid,
} from './components';
import assert from 'assert';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';
import { useFullscreenStripeLoader } from 'context';
import { useTranslation } from 'react-i18next';
interface Props {
  equipment: IEquipmentEntry;
  hideBasicCard?: boolean;
}

const PRICE_INFO_KEY_NAME = 'PREMIUM_TELEMATICS';

export default function SubscriptionPlanCards({
  equipment,
  hideBasicCard,
}: Props) {
  const { t } = useTranslation();
  const { addErrorModal } = useContext(GlobalErrorContext);

  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const isAnnuallyBilling = billingPeriod === 'annually';

  const getPricingInformationQuery = api.useGetPricingInformationQuery({
    keyName: PRICE_INFO_KEY_NAME,
    businessUnit: equipment.telematics?.businessUnit!,
  });

  const premiumBenefits = usePremiumBenefitsList();
  const basicBenefits = useBasicBenefitsList();

  const [createCheckoutSession, { isLoading: isCreatingCheckoutSession }] =
    api.useCreateCheckoutSessionMutation();

  const { show: showStripeLoader, hide: hideStripeLoader } =
    useFullscreenStripeLoader();

  const planPricing = useMemo(() => {
    return getPricingInformationQuery?.data?.reduce<PlanPricing>(
      (acc, info) => {
        const interval = info.recurring?.interval;
        const amount = new Intl.NumberFormat('en-US', {
          minimumFractionDigits: info.unit_amount % 100 === 0 ? 0 : 2,
          maximumFractionDigits: 2,
        }).format(info.unit_amount / 100);

        if (interval === 'year') {
          return { ...acc, annually: { id: info.id, amount } };
        }

        if (interval === 'month') {
          return { ...acc, monthly: { id: info.id, amount } };
        }

        return acc;
      },
      {
        annually: { id: 'initial_annually_id', amount: '' },
        monthly: {
          id: 'initial_monthly_id',
          amount: '',
        },
      },
    );
  }, [getPricingInformationQuery.data]);

  async function handleUpgrade() {
    assert(planPricing);
    const price = isAnnuallyBilling
      ? planPricing.annually.id
      : planPricing.monthly.id;
    const equipmentUrl = `${window.location.origin}/#/equipment/${equipment.id}`;

    showStripeLoader();

    try {
      const { checkoutUrl } = await createCheckoutSession({
        price,
        equipmentId: equipment.id,
        successUrl: `${equipmentUrl}/subscriptionSuccess`,
        cancelUrl: equipmentUrl,
      }).unwrap();

      window.location.href = checkoutUrl;
    } catch (error) {
      hideStripeLoader();
      addErrorModal();
    }
  }

  if (hideBasicCard) {
    return (
      <div className="subscription-plan-cards subscription-plan-cards__premium-only">
        <BillingSwitch
          billingPeriod={billingPeriod}
          planPricing={planPricing}
          isLoading={getPricingInformationQuery.isLoading}
          setBillingPeriod={setBillingPeriod}
        />

        <PremiumInfoPlanCard
          isAnnuallyBilling={isAnnuallyBilling}
          planPricing={planPricing}
          isLoading={getPricingInformationQuery.isLoading}
          isUpgrading={isCreatingCheckoutSession}
          onUpgrade={handleUpgrade}
        />
      </div>
    );
  }

  return (
    <>
      <div className="subscription-plan-cards__switch-container">
        <BillingSwitch
          billingPeriod={billingPeriod}
          planPricing={planPricing}
          isLoading={getPricingInformationQuery.isLoading}
          setBillingPeriod={setBillingPeriod}
        />
      </div>

      <SubscriptionPlanComparisonGrid
        card1={{
          title: (
            <>
              {t(
                'subscriptions.manage_your_plan.premium_telematics_plan.title',
              )}

              <PremiumPlanInfoTooltip />
            </>
          ),
          subtitle: (
            <span className="premium-info-plan-card__subtitle">
              <span className="premium-info-plan-card__price">
                $
                {planPricing
                  ? isAnnuallyBilling
                    ? planPricing.annually.amount
                    : planPricing.monthly.amount
                  : 0}
              </span>
              {t(
                `subscriptions.manage_your_plan.premium_telematics_plan.${
                  isAnnuallyBilling ? 'price_per_year' : 'price_per_month'
                }`,
              )}
            </span>
          ),
          isSubtitleLoading: getPricingInformationQuery.isLoading,
          buttonLabel: t(`subscriptions.manage_your_plan.upgrade`),
          isButtonLoading:
            getPricingInformationQuery.isLoading || isCreatingCheckoutSession,
          featuresTitle: {
            i18nKey:
              'subscriptions.manage_your_plan.premium_telematics_plan.access_to_all',
          },
          features: premiumBenefits,
          onUpgrade: handleUpgrade,
        }}
        card2={{
          isCurrent: true,
          title: t('subscriptions.manage_your_plan.basic_plan_view.title'),
          subtitle: (
            <span className="basic-info-plan-card__subtitle">
              {t('subscriptions.manage_your_plan.basic_plan_view.free')}
            </span>
          ),
          isSubtitleLoading: getPricingInformationQuery.isLoading,
          buttonLabel: t(
            'subscriptions.manage_your_plan.basic_plan_view.current_plan',
          ),
          isButtonLoading:
            getPricingInformationQuery.isLoading || isCreatingCheckoutSession,
          featuresTitle: t(
            'subscriptions.manage_your_plan.basic_plan_view.basic_plan_features',
          ),
          features: basicBenefits,
        }}
      />
    </>
  );
}
